import React, { useState, useCallback, useRef } from 'react';
import { Loader2, CheckCircle, XCircle, FolderOpen } from 'lucide-react';
import { useDriveSubFolders, useDriveFiles } from '@/hooks/useDocFlow';
import { DRIVE_ROOT_FOLDER_ID } from '@/types/docflow';
import type { IDriveFile } from '@/types/docflow';
import { fetchWithAuth } from '@/services/apiClient';
import { getFileContentUrl } from '@/services/docflowService';
import { preprocessInvoice, detectInvoiceType } from '@/services/invoiceService';

// Re-use a simplified version of the batch service types
interface ProcessingFile {
  file: IDriveFile;
  status: 'pending' | 'processing' | 'done' | 'error';
  error?: string;
  invoiceId?: string;
}

interface BatchInvoicePipelineProps {
  onComplete: () => void;
  onCancel: () => void;
}

type PipelineStep = 'folder-picker' | 'processing' | 'done';

const BatchInvoicePipeline: React.FC<BatchInvoicePipelineProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<PipelineStep>('folder-picker');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolderName, setSelectedFolderName] = useState('');
  const [files, setFiles] = useState<ProcessingFile[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const abortRef = useRef(false);

  // Start processing all files in the selected folder
  const startProcessing = async (folderId: string, folderFiles: IDriveFile[]) => {
    const processingFiles: ProcessingFile[] = folderFiles.map(f => ({
      file: f,
      status: 'pending' as const,
    }));
    setFiles(processingFiles);
    setStep('processing');
    abortRef.current = false;

    for (let i = 0; i < processingFiles.length; i++) {
      if (abortRef.current) break;

      setCurrentIndex(i);
      setFiles(prev => prev.map((f, idx) =>
        idx === i ? { ...f, status: 'processing' } : f
      ));

      try {
        // Download file content
        const url = getFileContentUrl(processingFiles[i].file.id);
        const response = await fetchWithAuth(url, {
          method: 'GET',
          headers: { 'Content-Type': '' },
        });
        const mimeType = response.headers.get('Content-Type') || 'application/pdf';
        const blob = await response.blob();
        const base64 = await blobToBase64(blob);

        // Call /detect (Cloud Run) — this does preprocess + AI classification + metadata
        const detectResult = await detectInvoiceType({
          file_base64: base64,
          mime_type: mimeType,
        });

        // Write draft to sheet via backend
        await fetchWithAuth('/api/v1/invoices/batch-draft-write', {
          method: 'POST',
          body: {
            source_file: processingFiles[i].file.name,
            source_file_id: processingFiles[i].file.id,
            detect_result: detectResult,
          },
        });

        setFiles(prev => prev.map((f, idx) =>
          idx === i ? {
            ...f,
            status: 'done',
            invoiceId: detectResult.metadata?.invoice_number?.value as string || '',
          } : f
        ));
      } catch (err) {
        setFiles(prev => prev.map((f, idx) =>
          idx === i ? {
            ...f,
            status: 'error',
            error: err instanceof Error ? err.message : 'Processing failed',
          } : f
        ));
      }
    }

    setStep('done');
  };

  const handleFolderSelect = async (folderId: string, folderName: string, folderFiles: IDriveFile[]) => {
    setSelectedFolderId(folderId);
    setSelectedFolderName(folderName);

    // Filter to PDF/image files
    const invoiceFiles = folderFiles.filter(f => {
      const mime = f.mime_type || '';
      const name = f.name.toLowerCase();
      return mime === 'application/pdf' || mime.startsWith('image/') ||
        name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
    });

    if (invoiceFiles.length === 0) {
      // No files to process
      return;
    }

    await startProcessing(folderId, invoiceFiles);
  };

  const doneCount = files.filter(f => f.status === 'done').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  switch (step) {
    case 'folder-picker':
      return (
        <FolderPickerDialog
          onSelect={handleFolderSelect}
          onCancel={onCancel}
        />
      );

    case 'processing':
    case 'done':
      return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={step === 'done' ? onComplete : undefined} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg mx-4 w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {step === 'done' ? 'Batch Processing Complete' : 'Processing Invoices...'}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                {selectedFolderName} — {doneCount}/{files.length} completed
                {errorCount > 0 && <span className="text-red-500 ml-2">{errorCount} errors</span>}
              </p>
            </div>

            {/* Progress bar */}
            <div className="px-6 pt-3">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${((doneCount + errorCount) / Math.max(files.length, 1)) * 100}%` }}
                />
              </div>
            </div>

            {/* File list */}
            <div className="flex-1 overflow-y-auto px-6 py-3 space-y-1.5">
              {files.map((f, i) => (
                <div key={f.file.id} className="flex items-center gap-2 text-sm">
                  {f.status === 'pending' && <div className="w-4 h-4 rounded-full border-2 border-gray-300" />}
                  {f.status === 'processing' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
                  {f.status === 'done' && <CheckCircle className="w-4 h-4 text-green-500" />}
                  {f.status === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
                  <span className={`flex-1 truncate ${
                    f.status === 'processing' ? 'text-blue-700 dark:text-blue-300 font-medium' :
                    f.status === 'error' ? 'text-red-600' :
                    f.status === 'done' ? 'text-gray-500' :
                    'text-gray-400'
                  }`}>
                    {f.file.name}
                  </span>
                  {f.invoiceId && (
                    <span className="text-xs text-green-600 shrink-0">{f.invoiceId}</span>
                  )}
                  {f.error && (
                    <span className="text-xs text-red-500 shrink-0 max-w-[150px] truncate" title={f.error}>{f.error}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              {step === 'processing' && (
                <button
                  onClick={() => { abortRef.current = true; }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Stop
                </button>
              )}
              {step === 'done' && (
                <button
                  onClick={onComplete}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Done — Review Drafts
                </button>
              )}
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Folder Picker Dialog (browse Drive, select folder, see file count)
// ---------------------------------------------------------------------------

interface FolderPickerDialogProps {
  onSelect: (folderId: string, folderName: string, files: IDriveFile[]) => void;
  onCancel: () => void;
}

const FolderPickerDialog: React.FC<FolderPickerDialogProps> = ({ onSelect, onCancel }) => {
  const [currentFolderId, setCurrentFolderId] = useState(DRIVE_ROOT_FOLDER_ID);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string; name: string }[]>([
    { id: DRIVE_ROOT_FOLDER_ID, name: 'Root' },
  ]);

  const { data: folders, isLoading: foldersLoading } = useDriveSubFolders(currentFolderId);
  const { data: files, isLoading: filesLoading } = useDriveFiles(currentFolderId);

  const navigateToFolder = useCallback((folderId: string, folderName: string) => {
    setCurrentFolderId(folderId);
    setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    const crumb = breadcrumbs[index];
    setCurrentFolderId(crumb.id);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  }, [breadcrumbs]);

  const isLoading = foldersLoading || filesLoading;

  // Count invoice files (PDF/images)
  const invoiceFiles = (files || []).filter(f => {
    const mime = f.mime_type || '';
    const name = f.name.toLowerCase();
    return mime === 'application/pdf' || mime.startsWith('image/') ||
      name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
  });

  const currentFolderName = breadcrumbs[breadcrumbs.length - 1]?.name || 'Root';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-xl mx-4 w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select Invoice Folder</h2>
          <p className="text-xs text-gray-500 mt-1">Browse to the folder containing invoices to process</p>
        </div>

        {/* Breadcrumbs */}
        <div className="flex items-center gap-1 px-6 py-2 bg-gray-50 dark:bg-gray-900 text-xs overflow-x-auto border-b border-gray-200 dark:border-gray-700">
          {breadcrumbs.map((crumb, i) => (
            <span key={crumb.id} className="flex items-center gap-1 whitespace-nowrap">
              {i > 0 && <span className="text-gray-400">/</span>}
              <button
                onClick={() => navigateToBreadcrumb(i)}
                className={`hover:text-blue-600 ${
                  i === breadcrumbs.length - 1 ? 'text-blue-600 font-medium' : 'text-gray-500'
                }`}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </div>

        {/* Folder list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {(folders || []).map(folder => (
                <button
                  key={folder.id}
                  onClick={() => navigateToFolder(folder.id, folder.name)}
                  className="w-full flex items-center gap-3 px-6 py-3 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <FolderOpen className="w-4 h-4 text-blue-500 shrink-0" />
                  <span className="truncate text-gray-700 dark:text-gray-200">{folder.name}</span>
                </button>
              ))}
              {(folders || []).length === 0 && invoiceFiles.length === 0 && (
                <div className="text-sm text-gray-400 text-center py-10">Empty folder</div>
              )}
            </div>
          )}
        </div>

        {/* Footer — shows file count + process button */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {invoiceFiles.length > 0
              ? <span className="text-blue-600 font-medium">{invoiceFiles.length} invoice file(s) in this folder</span>
              : <span>No invoice files here — navigate to a folder with PDFs/images</span>
            }
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              Cancel
            </button>
            <button
              onClick={() => onSelect(currentFolderId, currentFolderName, invoiceFiles)}
              disabled={invoiceFiles.length === 0}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Process {invoiceFiles.length} File(s)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper: blob to base64
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default BatchInvoicePipeline;
